// src/components/MemoryGameScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiAward, FiZap } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

// أيقونات للبطاقات
const CARD_ICONS = [
  '🎈', '🎨', '🎭', '🎪', '🎯', '🎲', '🎸', '🎺',
  '🌟', '🌈', '🌺', '🌻', '🍎', '🍕', '🍦', '🎂'
];

const MemoryGameScreen = () => {
  const navigate = useNavigate();
  const { userType } = useAuth();
  
  // حالة اللعبة
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [timeLeft, setTimeLeft] = useState(90); // تم تقليل الوقت قليلاً لتناسب عدد البطاقات الأقل
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [bestScore, setBestScore] = useState(localStorage.getItem('memoryGameBestScore') || null);
  
  // تهيئة اللعبة
  const initializeGame = useCallback(() => {
    // تعديل: استخدام 9 أيقونات فقط لإنشاء 18 بطاقة (6 أعمدة × 3 صفوف)
    const gameIcons = CARD_ICONS.slice(0, 9); 
    const duplicatedIcons = [...gameIcons, ...gameIcons];
    
    const shuffledCards = duplicatedIcons
      .sort(() => Math.random() - 0.5)
      .map((icon, index) => ({
        id: index,
        icon: icon,
        isFlipped: false,
        isMatched: false
      }));
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setGameWon(false);
    setTimeLeft(90);
    setGameStarted(false);
    setGameOver(false);
  }, []);
  
  const startGame = () => {
    initializeGame();
    setGameStarted(true);
  };
  
  useEffect(() => {
    if (gameStarted && timeLeft > 0 && !gameWon && !gameOver) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !gameWon) {
      setGameOver(true);
      setGameStarted(false);
    }
  }, [timeLeft, gameStarted, gameWon, gameOver]);
  
  useEffect(() => {
    if (matchedCards.length > 0 && matchedCards.length === cards.length && cards.length > 0) {
      setGameWon(true);
      setGameStarted(false);
      const currentScore = moves;
      if (!bestScore || currentScore < bestScore) {
        setBestScore(currentScore);
        localStorage.setItem('memoryGameBestScore', currentScore);
      }
    }
  }, [matchedCards, cards, moves, bestScore]);
  
  const handleCardClick = (cardId) => {
    if (!gameStarted || gameOver) return;
    const card = cards.find(c => c.id === cardId);
    if (card.isMatched || card.isFlipped) return;
    if (flippedCards.length === 2) return;
    
    const newCards = cards.map(c => 
      c.id === cardId ? { ...c, isFlipped: true } : c
    );
    setCards(newCards);
    
    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);
    
    if (newFlippedCards.length === 2) {
      setMoves(moves + 1);
      
      const [firstId, secondId] = newFlippedCards;
      const firstCard = newCards.find(c => c.id === firstId);
      const secondCard = newCards.find(c => c.id === secondId);
      
      if (firstCard.icon === secondCard.icon) {
        setTimeout(() => {
          const updatedCards = cards.map(c => 
            c.id === firstId || c.id === secondId 
              ? { ...c, isMatched: true } 
              : c
          );
          setCards(updatedCards);
          setMatchedCards([...matchedCards, firstId, secondId]);
          setFlippedCards([]);
        }, 600);
      } else {
        setTimeout(() => {
          const updatedCards = cards.map(c => 
            c.id === firstId || c.id === secondId 
              ? { ...c, isFlipped: false } 
              : c
          );
          setCards(updatedCards);
          setFlippedCards([]);
        }, 1000);
      }
    }
  };
  
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  const navigateBack = () => {
    if (userType === 'provider') {
      navigate('/provider');
    } else {
      navigate('/client');
    }
  };
  
  return (
    <div className="h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-2 sm:p-4 overflow-hidden flex flex-col">
      <div className="max-w-md mx-auto w-full flex flex-col h-full">
        {/* رأس الصفحة */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <button
            onClick={navigateBack}
            className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
          >
            <FiArrowLeft className="text-gray-700" />
          </button>
          
          <h1 className="text-xl font-bold text-gray-800">لعبة الذاكرة</h1>
          
          <button
            onClick={initializeGame}
            className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
          >
            <FiRefreshCw className="text-gray-700" />
          </button>
        </div>
        
        {/* معلومات اللعبة */}
        <div className="bg-white rounded-xl shadow-md p-2 mb-2 flex-shrink-0">
          <div className="flex justify-between items-center text-sm">
            <div className="text-center px-1">
              <p className="text-gray-500 text-xs">الوقت</p>
              <p className={`font-bold ${timeLeft <= 10 ? 'text-red-500' : 'text-gray-800'}`}>
                {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
              </p>
            </div>
            
            <div className="text-center px-1">
              <p className="text-gray-500 text-xs">الحركات</p>
              <p className="font-bold text-gray-800">{moves}</p>
            </div>
            
            <div className="text-center px-1">
              <p className="text-gray-500 text-xs">الأفضل</p>
              <p className="font-bold text-gray-800">{bestScore || '-'}</p>
            </div>
          </div>
        </div>
        
        {/* منطقة المحتوى الرئيسية */}
        <div className="flex-1 flex flex-col justify-center overflow-hidden relative">
          
          {/* شاشة البداية */}
          {!gameStarted && !gameWon && !gameOver && (
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <FiZap className="text-5xl text-yellow-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-gray-800 mb-2">اختبار الذاكرة</h2>
              <p className="text-gray-600 text-sm mb-4">
                اقلب البطاقات وابحث عن الأزواج المتطابقة!
              </p>
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white py-2 px-6 rounded-full font-medium hover:from-purple-600 hover:to-blue-600 transition-colors"
              >
                ابدأ اللعبة
              </button>
            </div>
          )}
          
          {/* شاشة انتهاء الوقت */}
          {gameOver && (
            <div className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="text-5xl mb-3">⏰</div>
              <h2 className="text-lg font-bold text-gray-800 mb-2">انتهى الوقت!</h2>
              <p className="text-gray-600 text-sm mb-4">
                قمت بتنفيذ {moves} حركة. حاول مرة أخرى!
              </p>
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white py-2 px-6 rounded-full font-medium hover:from-purple-600 hover:to-blue-600 transition-colors"
              >
                حاول مرة أخرى
              </button>
            </div>
          )}
          
          {/* شاشة الفوز */}
          {gameWon && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-md p-6 text-center relative overflow-hidden"
            >
              <div className="relative z-10">
                <FiAward className="text-5xl text-yellow-500 mx-auto mb-3" />
                <h2 className="text-lg font-bold text-gray-800 mb-2">تهانينا!</h2>
                <p className="text-gray-600 text-sm mb-1">
                  لقد فزت في {moves} حركة!
                </p>
                {bestScore === moves && (
                  <p className="text-green-600 font-medium text-sm mb-3">
                    هذه أفضل نتيجة لك!
                  </p>
                )}
                <div className="flex justify-center space-x-3 space-x-reverse">
                  <button
                    onClick={startGame}
                    className="bg-gradient-to-r from-purple-500 to-blue-500 text-white py-2 px-4 rounded-full font-medium hover:from-purple-600 hover:to-blue-600 transition-colors text-sm"
                  >
                    العب مرة أخرى
                  </button>
                  <button
                    onClick={navigateBack}
                    className="bg-gray-200 text-gray-800 py-2 px-4 rounded-full font-medium hover:bg-gray-300 transition-colors text-sm"
                  >
                    الرئيسية
                  </button>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* لوحة اللعبة - تم التعديل لـ 6 أعمدة و 3 صفوف */}
          {gameStarted && (
            <div className="grid grid-cols-6 grid-rows-3 gap-2 h-full w-full pb-2">
              <AnimatePresence>
                {cards.map((card) => (
                  <motion.div
                    key={card.id}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleCardClick(card.id)}
                    className="h-full w-full flex items-center justify-center rounded-lg text-lg sm:text-xl font-bold cursor-pointer select-none"
                    style={{ height: '100%' }} // التأكد من ملء الارتفاع
                  >
                    <motion.div
                      animate={{ rotateY: card.isFlipped || card.isMatched ? 180 : 0 }}
                      transition={{ duration: 0.4 }}
                      style={{ 
                        transformStyle: 'preserve-3d',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '0.5rem'
                      }}
                      className={`${
                        card.isFlipped || card.isMatched
                          ? 'bg-gradient-to-br from-purple-400 to-blue-400 text-white shadow-md'
                          : 'bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 hover:from-gray-300 hover:to-gray-400 shadow-sm'
                      } ${card.isMatched ? 'ring-2 ring-green-400 ring-opacity-50' : ''}`}
                    >
                      {card.isFlipped || card.isMatched ? card.icon : '?'}
                    </motion.div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryGameScreen;